import { createFileRoute, Link } from "@tanstack/react-router";
import heroValues from "@/assets/hero-our-values.jpg";
import valueImg4 from "@/assets/lifestyle-4.jpg";
import creedBg from "@/assets/built-for-western.jpg";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { DEFAULT_SITE_SETTINGS } from "@/lib/siteSettings";

export const Route = createFileRoute("/our-values")({
  head: () => ({
    meta: [
      { title: "Our Values | Coyote Creek Hat Co." },
      { name: "description", content: "Faith, tradition, the sporting spirit, and uncompromising craftsmanship — the values that drive Coyote Creek Hat Co." },
      { property: "og:title", content: "Our Values | Coyote Creek Hat Co." },
      { property: "og:description", content: "The values that drive Coyote Creek Hat Co." },
      { property: "og:url", content: "https://coyotecreekhatco.com/our-values" },
      { property: "og:image", content: `https://coyotecreekhatco.com${heroValues}` },
      { name: "twitter:image", content: `https://coyotecreekhatco.com${heroValues}` },
    ],
    links: [{ rel: "canonical", href: "https://coyotecreekhatco.com/our-values" }],
  }),
  component: OurValuesPage,
});

const VALUES = [
  {
    n: "I",
    title: "Faith & Family",
    desc: "Grounded in strong, enduring principles and a deep respect for American heritage.",
    img: "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Image-6.jpg?v=1696479784",
    creed: "Rooted in faith. Carried by tradition.",
  },
  {
    n: "II",
    title: "The Sporting Spirit",
    desc: "Celebrating the drive, camaraderie, and tradition of sportsmanship, from the duck blind to the golf course.",
    img: "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/BE522DDA-7AF4-45D4-8E50-2E18E2E829FC.png?v=1765165220",
    creed: "From the blind to the back nine.",
  },
  {
    n: "III",
    title: "Uncompromising Craftsmanship",
    desc: "Designing premium, durable goods that look as good on the weekend as they do in the backcountry.",
    img: "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/89A71251-566C-451F-A4B5-FE1062BC59EC.png?v=1722660516",
    creed: "No shortcuts. No compromises.",
  },
  {
    n: "IV",
    title: "Authenticity",
    desc: "Remaining true to our roots—approachable, genuine, and unapologetically bold.",
    img: valueImg4,
    creed: "True to our roots. Always.",
  },
];

const MARQUEE_ITEMS = [
  "Faith & Family",
  "The Sporting Spirit",
  "Uncompromising Craftsmanship",
  "Authenticity",
];

function OurValuesPage() {
  const { data: settings } = useSiteSettings();
  const marqueeBg = settings?.feature_marquee_bg_color ?? DEFAULT_SITE_SETTINGS.feature_marquee_bg_color;
  const marqueeText = settings?.feature_marquee_text_color ?? DEFAULT_SITE_SETTINGS.feature_marquee_text_color;
  return (
    <div className="bg-background">
      {/* 1. HERO */}
      <div className="relative w-full h-[50vh] min-h-[320px] overflow-hidden">
        <img
          src={heroValues}
          alt="Our Values"
          className="absolute inset-0 h-full w-full object-cover"
          width={1920}
          height={768}
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="font-heading text-5xl md:text-7xl uppercase tracking-[0.15em] text-white text-center px-6 drop-shadow-lg">
            Our Values
          </h1>
        </div>
        {/* Scroll cue */}
        <div className="absolute bottom-6 right-6 z-10 hidden flex-col items-center gap-3 md:flex">
          <span className="font-ui text-[10px] font-bold uppercase tracking-[0.3em] text-white/90 [writing-mode:vertical-rl]">
            Scroll
          </span>
          <span className="block h-12 w-px bg-white/40" />
        </div>
      </div>

      {/* 2. INTRO MANIFESTO */}
      <section className="bg-[#E8DCC4] py-24 md:py-36">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="flex items-center gap-4">
            <span className="block h-px w-12 bg-primary/30" />
            <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/60">
              01 / What We Stand For
            </span>
          </div>

          <p className="mt-10 font-heading text-3xl leading-[1.15] tracking-tight text-primary sm:text-4xl md:text-5xl lg:text-6xl uppercase">
            WE BELIEVE IN WEARING SOMETHING YOU STAND FOR. OUR HATS ARE BUILT ON THE SAME VALUES THAT BUILT OUR COMMUNITY —{" "}
            <span className="italic" style={{ color: "#fa5021" }}>
              FAITH, FAMILY, HARD WORK,
            </span>{" "}
            AND THE OUTDOORS.
          </p>

          <div className="mt-12 flex justify-end">
            <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/70">
              — The Coyote Creek Community
            </span>
          </div>
        </div>
      </section>

      {/* 3. CORE VALUES — ZIGZAG */}
      <section className="bg-background py-24 md:py-32">
        <div className="mx-auto max-w-[1300px] px-6">
          <div className="mb-16 flex flex-col items-center text-center md:mb-24">
            <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/60">
              02 / Core Values
            </span>
            <h2 className="mt-6 font-heading text-4xl font-black uppercase tracking-tight text-primary md:text-6xl">
              Four Truths
            </h2>
            <div
              className="mt-6 h-1 w-12"
              style={{ backgroundColor: "#fa5021" }}
            />
          </div>

          <div className="space-y-20 md:space-y-32">
            {VALUES.map((v, i) => {
              const flipped = i % 2 === 1;
              return (
                <div
                  key={v.title}
                  className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16"
                >
                  {/* Image */}
                  <div className={`relative ${flipped ? "lg:order-2" : ""}`}>
                    <div className="aspect-[4/5] w-full overflow-hidden bg-muted">
                      <img
                        src={v.img}
                        alt={`${v.title} — placeholder`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <span
                      className={`absolute -top-6 font-heading text-7xl font-black italic leading-none md:-top-10 md:text-9xl ${
                        flipped ? "-right-2 md:-right-6" : "-left-2 md:-left-6"
                      }`}
                      style={{ color: "#fa5021" }}
                      aria-hidden
                    >
                      {v.n}
                    </span>
                  </div>

                  {/* Text */}
                  <div className={flipped ? "lg:order-1 lg:pr-8" : "lg:pl-8"}>
                    <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/50">
                      Value {v.n}
                    </span>
                    <h3 className="mt-4 font-heading text-3xl font-black uppercase tracking-tight text-primary md:text-5xl">
                      {v.title}
                    </h3>
                    <div
                      className="mt-6 h-px w-16"
                      style={{ backgroundColor: "#fa5021" }}
                    />
                    <p className="mt-6 font-body text-base leading-relaxed text-muted-foreground md:text-lg">
                      {v.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. MARQUEE BAND */}
      <section className="overflow-hidden py-5" style={{ backgroundColor: marqueeBg, color: marqueeText }}>
        <div className="flex w-max animate-marquee whitespace-nowrap will-change-transform">
          {[0, 1, 2].map((g) => (
            <div key={g} className="flex shrink-0 items-center gap-8 px-4">
              {MARQUEE_ITEMS.map((item) => (
                <span key={item} className="flex shrink-0 items-center gap-8">
                  <span className="font-ui text-xs font-bold uppercase tracking-[0.25em]">
                    {item}
                  </span>
                  <span className="text-sm">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>


      {/* 6. CLOSING CTA */}
      <section className="bg-secondary py-28 text-primary md:py-36">
        <div className="mx-auto max-w-[900px] px-6 text-center">
          <p className="font-heading text-3xl italic leading-[1.1] tracking-tight sm:text-4xl md:text-5xl">
            Authentic. Rooted. Built to last.
          </p>
          <div className="mx-auto mt-10 h-px w-20 bg-primary/40" />
          <Link
            to="/the-standard"
            className="mt-12 btn-cc-primary tracking-[0.2em]"
          >
            See The Standard
          </Link>
        </div>
      </section>
    </div>
  );
}
